import { graphql } from '../../../gql';
import { useModalContext } from '../../../modal/ModalContext';
import { ModalActions } from '../../../modal/components/ModalActions';
import { ModalContent } from '../../../modal/components/ModalContent';
import { useApiMutation } from '../../../query/hooks';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../../../store/snackbar';
import { mutationResponseHandler } from '../../../utils/mutationResponseHandler';
import { z } from 'zod';

const deleteMetricTemplateMutation = graphql(`
	mutation settings_deleteMetricTemplate($id: UUID!) {
		deleteMetricTemplate(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const deleteMetricTemplateModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const DeleteMetricTemplateModal: FC<z.infer<typeof deleteMetricTemplateModalSchema>> = ({ id, name }) => {
	const intl = useIntl();

	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { mutate: deleteMetricTemplate } = useApiMutation(deleteMetricTemplateMutation, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.deleteMetricTemplate.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'metric-template.success.delete' }),
					error: intl.formatMessage({ id: 'metric-template.error.delete' }),
				},
				errorMessage: result.deleteMetricTemplate.errorMessage,
				openSnackbar,
			});
		},
	});

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'metric-template.delete-modal.title' })}
				description={intl.formatMessage({ id: 'metric-template.delete-modal.description' }, { name })}
			/>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteMetricTemplate({ id });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
			/>
		</>
	);
};

export { deleteMetricTemplateModalSchema, DeleteMetricTemplateModal };
